// Reynolds-style steering simulator for the landing-page bubbles.
// Per frame: each bubble accumulates force from a divergence-free curl
// flow field (the engine), plus four steering corrections (UI avoid,
// cursor flee, pair separate, orbit bias). Most forces live in
// bubble-forces; pair separation is here because it's the only term
// that needs the full bubble list at once.

import { uiAvoid, cursorFlee, orbitBias } from './bubble-forces';
import { flowFollow } from './bubble-flow';
import { SEP_FORCE, SEP_RANGE_FACTOR, VEL_DAMP } from './bubble-constants';

export type Vec2 = { x: number; y: number };
export type Rect = { x: number; y: number; w: number; h: number };

export type Bubble = {
  type: 'photo' | 'emoji';
  photoUrl?: string;
  pos: Vec2;
  vel: Vec2;
  size: number;
  depth: number; // 0 = far, 1 = near; drives size/opacity/blur/maxSpeed
  maxSpeed: number; // derived from depth at spawn
  layer: number; // index into DEPTH_LAYERS; pair separation is per-layer
};

export type FlowCtx = {
  cursor: Vec2 | null;
  anchors: Rect[];
  width: number;
  height: number;
  time: number;
};

function bounceWalls(b: Bubble, w: number, h: number): void {
  const maxX = w - b.size;
  const maxY = h - b.size;
  if (b.pos.x < 0 || b.pos.x > maxX) b.vel.x *= -0.4;
  if (b.pos.y < 0 || b.pos.y > maxY) b.vel.y *= -0.4;
  b.pos.x = Math.max(0, Math.min(maxX, b.pos.x));
  b.pos.y = Math.max(0, Math.min(maxY, b.pos.y));
}

function pairSeparate(bubbles: Bubble[]): Vec2[] {
  const out: Vec2[] = bubbles.map(() => ({ x: 0, y: 0 }));
  for (let i = 0; i < bubbles.length; i++) {
    const a = bubbles[i];
    const oi = out[i];
    if (!a || !oi) continue;
    for (let j = 0; j < bubbles.length; j++) {
      if (j === i) continue;
      const o = bubbles[j];
      if (!o || o.layer !== a.layer) continue;
      const dx = a.pos.x - o.pos.x;
      const dy = a.pos.y - o.pos.y;
      const d = Math.hypot(dx, dy);
      const minD = ((a.size + o.size) / 2) * SEP_RANGE_FACTOR;
      if (d <= 0 || d >= minD) continue;
      const t = 1 - d / minD;
      oi.x += (dx / d) * SEP_FORCE * t;
      oi.y += (dy / d) * SEP_FORCE * t;
    }
  }
  return out;
}

function applyForcesTo(b: Bubble, ctx: FlowCtx, pair: Vec2): Vec2 {
  const flow = flowFollow(b, ctx);
  const ui = uiAvoid(b, ctx.anchors);
  const cur = ctx.cursor ? cursorFlee(b, ctx.cursor) : { x: 0, y: 0 };
  const orb = orbitBias(b, ctx.anchors);
  return {
    x: flow.x + ui.x + cur.x + pair.x + orb.x,
    y: flow.y + ui.y + cur.y + pair.y + orb.y,
  };
}

function integrate(b: Bubble, a: Vec2): void {
  b.vel.x += a.x;
  b.vel.y += a.y;
  const speed = Math.hypot(b.vel.x, b.vel.y);
  if (speed > b.maxSpeed) {
    b.vel.x = (b.vel.x / speed) * b.maxSpeed;
    b.vel.y = (b.vel.y / speed) * b.maxSpeed;
  }
  b.pos.x += b.vel.x;
  b.pos.y += b.vel.y;
  b.vel.x *= VEL_DAMP;
  b.vel.y *= VEL_DAMP;
}

// Smoothly remap every bubble's position + velocity when the
// container's aspect ratio / size changes. Each frame the loop
// compares the current width/height against the previous frame's and
// passes the ratio here; pixel positions follow the viewport instead
// of clipping or trailing off the new bounds.
export function rescaleBubbles(
  bubbles: Bubble[],
  sx: number,
  sy: number
): void {
  if (sx === 1 && sy === 1) return;
  for (let i = 0; i < bubbles.length; i++) {
    const b = bubbles[i];
    if (!b) continue;
    b.pos.x *= sx;
    b.pos.y *= sy;
    b.vel.x *= sx;
    b.vel.y *= sy;
  }
}

export function stepAll(bubbles: Bubble[], ctx: FlowCtx): void {
  const pairs = pairSeparate(bubbles);
  for (let i = 0; i < bubbles.length; i++) {
    const b = bubbles[i];
    const p = pairs[i];
    if (!b || !p) continue;
    integrate(b, applyForcesTo(b, ctx, p));
    bounceWalls(b, ctx.width, ctx.height);
  }
}
